import React, { useMemo } from 'react';

import type { RubricCriterion, RubricDraft, RubricProjectRole } from '../types/rubricDraft';
import {
  RUBRIC_ROLE_ALL,
  filterCriteriaForRoles,
  formatRubricSubjectHeader,
  formatRubricTargetLevelsHeader,
  getMaxPointsForLevels,
  normalizeApplicableRoleIds,
  normalizeCriterionWeight,
  resolvePrintedSubjectTags,
  resolveRubricOas,
  rubricEvaluationTypeLabel,
} from '../types/rubricDraft';
import { getSubjectNameByTag } from '../constants/subjectCatalog';
import { EXAM_LEVEL_OPTIONS } from '../constants/examAiParams';

interface PrintRubricDocumentProps {
  rubric: RubricDraft;
  courseDisplayName?: string;
  courseLevelLabel?: string;
  /** Id de nivel del curso (p. ej. 1basico) — fallback de diagnóstica. */
  courseLevelId?: string | null;
  /** Asignatura del curso (fallback si no hay modo inter/multi). */
  subjectName?: string;
  /** Tag de asignatura del curso — para listar incorporadas en inter/multi. */
  courseSubjectTag?: string | null;
  /** Tags adicionales del curso (profesora generalista). */
  courseAdditionalSubjectTags?: string[];
  /** Nombre del colegio — masthead arriba-izquierda (sin Edusico). */
  schoolName?: string;
  /** Logo del colegio (data URL). Si hay, reemplaza la letra inicial del masthead. */
  schoolLogoDataUrl?: string;
  /** Nombre del docente autenticado. */
  teacherName?: string;
  /** Filtro de impresión: 'all' o id de rol. */
  roleFilter?: 'all' | string;
}

type CriterionGroup = {
  key: string;
  label: string | null;
  criteria: RubricCriterion[];
};

function dominantRoleKey(criterion: RubricCriterion): string {
  const ids = normalizeApplicableRoleIds(criterion.applicableRoleIds);
  if (ids.includes(RUBRIC_ROLE_ALL) || !ids.length) return RUBRIC_ROLE_ALL;
  return ids[0];
}

function groupCriteriaByDominantRole(
  criteria: RubricCriterion[],
  roles: RubricProjectRole[],
  roleFilter: string,
): CriterionGroup[] {
  if (roleFilter !== 'all' || !roles.length) {
    return [{ key: 'flat', label: null, criteria }];
  }

  const byKey = new Map<string, RubricCriterion[]>();
  for (const criterion of criteria) {
    const key = dominantRoleKey(criterion);
    const list = byKey.get(key) ?? [];
    list.push(criterion);
    byKey.set(key, list);
  }

  const groups: CriterionGroup[] = [];
  const transversal = byKey.get(RUBRIC_ROLE_ALL);
  if (transversal?.length) {
    groups.push({ key: RUBRIC_ROLE_ALL, label: 'Todos los roles', criteria: transversal });
  }
  for (const role of roles) {
    const list = byKey.get(role.id);
    if (list?.length) {
      groups.push({ key: role.id, label: role.name, criteria: list });
    }
  }
  for (const [key, list] of byKey) {
    if (key === RUBRIC_ROLE_ALL) continue;
    if (roles.some((role) => role.id === key)) continue;
    groups.push({ key, label: 'Otros', criteria: list });
  }
  return groups.length ? groups : [{ key: 'flat', label: null, criteria }];
}

function computeMaxScore(criteria: RubricCriterion[], levels: RubricDraft['levels']): number {
  const maxLevel = getMaxPointsForLevels(levels);
  return criteria.reduce(
    (sum, criterion) => sum + maxLevel * normalizeCriterionWeight(criterion.weight),
    0,
  );
}

function formatOaLine(oa: { code: string; text: string }): string {
  const code = oa.code.trim();
  const text = oa.text.trim();
  if (code && text) return `${code} — ${text}`;
  return text || code || '—';
}

function resolvePrintedObjective(rubric: RubricDraft): string {
  const printed = String(rubric.evaluationObjective || '').trim();
  if (printed) return printed;
  // Legacy: no usar el brief crudo si es muy largo / parece prompt.
  const brief = String(rubric.objective || '').trim();
  if (brief && brief.length <= 160 && !/\n/.test(brief)) return brief;
  return '';
}

function RubricMatrixTable({
  rubric,
  criteria,
  criterionGroups,
  className,
}: {
  rubric: RubricDraft;
  criteria: RubricCriterion[];
  criterionGroups: CriterionGroup[];
  className: string;
}) {
  const levelCount = Math.max(1, rubric.levels.length);
  const colSpan = 1 + levelCount;

  return (
    <table className={className}>
      <thead>
        <tr>
          <th scope="col" rowSpan={2} className={`${className}__corner`}>
            Criterio de evaluación
          </th>
          <th scope="col" colSpan={levelCount} className={`${className}__levels-head`}>
            Niveles de logro
          </th>
        </tr>
        <tr>
          {rubric.levels.map((level) => (
            <th key={level.id} scope="col" className={`${className}__level`}>
              <span className={`${className}__level-name`}>{level.name}</span>
              {level.points != null && Number.isFinite(Number(level.points)) ? (
                <span className={`${className}__level-points`}>{level.points} pts</span>
              ) : null}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {criteria.length === 0 ? (
          <tr>
            <td colSpan={colSpan} className={`${className}__empty`}>
              No hay criterios para este filtro de rol.
            </td>
          </tr>
        ) : (
          criterionGroups.map((group) => (
            <React.Fragment key={group.key}>
              {group.label ? (
                <tr className={`${className}__category-row`}>
                  <th scope="rowgroup" colSpan={colSpan} className={`${className}__category`}>
                    {group.label}
                  </th>
                </tr>
              ) : null}
              {group.criteria.map((criterion) => (
                <tr key={criterion.id} className={`${className}__row`}>
                  <th scope="row" className={`${className}__criterion`}>
                    <p className={`${className}__criterion-name`}>
                      {criterion.name.trim() || 'Criterio sin nombre'}
                    </p>
                    {criterion.description.trim() ? (
                      <p className={`${className}__criterion-desc`}>{criterion.description.trim()}</p>
                    ) : null}
                  </th>
                  {rubric.levels.map((level) => (
                    <td key={`${criterion.id}-${level.id}`} className={`${className}__cell`}>
                      {(criterion.cells[level.id] ?? '').trim() || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))
        )}
      </tbody>
    </table>
  );
}

/** Layout simple (pre-editorial): sin masthead de colegio/OA. */
function ClassicRubricPrint({
  rubric,
  title,
  printedObjective,
  typeLabel,
  courseFilled,
  subjectLabel,
  levelsLabel,
  roleMeta,
  criteria,
  criterionGroups,
  maxScore,
}: {
  rubric: RubricDraft;
  title: string;
  printedObjective: string;
  typeLabel: string;
  courseFilled: string;
  subjectLabel: string;
  levelsLabel?: string;
  roleMeta: RubricProjectRole | null | undefined;
  criteria: RubricCriterion[];
  criterionGroups: CriterionGroup[];
  maxScore: number;
}) {
  return (
    <article className="print-worksheet print-rubric-sheet print-rubric-classic">
      <header className="print-rubric-classic__head">
        <p className="print-rubric-classic__eyebrow">
          {[typeLabel, subjectLabel, levelsLabel || courseFilled].filter(Boolean).join(' · ')}
          {roleMeta ? ` · ${roleMeta.name}` : ''}
        </p>
        <h1 className="print-rubric-classic__title">{title}</h1>
        <div className="print-rubric-classic__fields">
          <span>
            Nombre: <span className="print-rubric-classic__line" />
          </span>
          <span>
            Curso: <span className="print-rubric-classic__line print-rubric-classic__line--short" />
          </span>
          <span>
            Fecha: <span className="print-rubric-classic__line print-rubric-classic__line--short" />
          </span>
        </div>
        <div className="print-rubric-classic__scores" aria-label="Puntaje">
          <div className="print-rubric-classic__score-box">
            <span className="print-rubric-classic__score-label">Puntaje máx.</span>
            <strong className="print-rubric-classic__score-value">{maxScore || '—'}</strong>
          </div>
          <div className="print-rubric-classic__score-box print-rubric-classic__score-box--blank">
            <span className="print-rubric-classic__score-label">Puntaje obtenido</span>
            <span className="print-rubric-classic__score-blank" aria-hidden />
          </div>
        </div>
      </header>

      {printedObjective ? (
        <section className="print-rubric-classic__objective" aria-label="Objetivo de la evaluación">
          <h2>Objetivo de la evaluación</h2>
          <p>{printedObjective}</p>
        </section>
      ) : null}

      <RubricMatrixTable
        rubric={rubric}
        criteria={criteria}
        criterionGroups={criterionGroups}
        className="print-rubric-matrix"
      />

      <section className="print-rubric-classic__obs" aria-label="Observaciones">
        <h2>Observaciones</h2>
        <div className="print-rubric-classic__obs-box" aria-hidden>
          <span />
          <span />
          <span />
        </div>
      </section>

      <footer className="print-worksheet__footer">
        <p>
          {criteria.length} criterios · {rubric.levels.length} niveles de logro
        </p>
        <p className="print-worksheet__footer-brand">Edusico</p>
      </footer>
    </article>
  );
}

/** Layout editorial MINEDUC (con OA / encabezado de establecimiento). */
function EditorialRubricPrint({
  rubric,
  title,
  objective,
  printedObjective,
  oas,
  courseFilled,
  schoolLabel,
  schoolLogoDataUrl,
  teacherLabel,
  subjectLabels,
  roleMeta,
  criteria,
  criterionGroups,
  maxScore,
}: {
  rubric: RubricDraft;
  title: string;
  objective: string;
  printedObjective: string;
  oas: ReturnType<typeof resolveRubricOas>;
  courseFilled: string;
  schoolLabel: string;
  schoolLogoDataUrl?: string;
  teacherLabel: string;
  subjectLabels: string[];
  roleMeta: RubricProjectRole | null | undefined;
  criteria: RubricCriterion[];
  criterionGroups: CriterionGroup[];
  maxScore: number;
}) {
  const levelCount = Math.max(1, rubric.levels.length);
  const colSpan = 1 + levelCount;
  const typeShort =
    rubricEvaluationTypeLabel(rubric.evaluationType).replace(/^Evaluación\s+/i, '') || 'Rúbrica';
  const logoSrc = schoolLogoDataUrl?.trim() || '';

  const indicadores = criteria
    .map((criterion) => ({
      id: criterion.id,
      name: criterion.name.trim(),
    }))
    .filter((item) => item.name);

  const oaRows =
    oas.length > 0
      ? oas.map((oa) => ({
          axis: oa.axis?.trim() || rubric.curriculumTarget?.axis?.trim() || '—',
          text: formatOaLine(oa),
        }))
      : [{ axis: '—', text: printedObjective || '—' }];

  const showObjectiveExtra =
    Boolean(printedObjective) &&
    oas.length > 0 &&
    !oas.some((oa) => oa.text.trim() === printedObjective) &&
    printedObjective !== objective;

  return (
    <article className="print-worksheet print-rubric-sheet print-rubric-editorial">
      <header className="rubric-ed-header" aria-label="Encabezado de rúbrica">
        <div className="rubric-ed-header__row rubric-ed-header__row--top">
          <div className="rubric-ed-header__logo" aria-hidden>
            {logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                className="rubric-ed-header__logo-img"
              />
            ) : (
              <span className="rubric-ed-header__logo-mark">
                {(schoolLabel || 'E').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="rubric-ed-header__school">
            <p className="rubric-ed-header__school-kicker">Establecimiento</p>
            <p className="rubric-ed-header__school-name">{schoolLabel}</p>
          </div>
          <div className="rubric-ed-header__subjects">
            <p className="rubric-ed-header__subjects-kicker">
              Asignatura{subjectLabels.length > 1 ? 's' : ''}
            </p>
            <ul className="rubric-ed-header__subjects-list">
              {subjectLabels.length > 0 ? (
                subjectLabels.map((label, index) => (
                  <li
                    key={`${label}-${index}`}
                    className={
                      index === 0
                        ? 'rubric-ed-header__subject-item rubric-ed-header__subject-item--primary'
                        : 'rubric-ed-header__subject-item'
                    }
                  >
                    {label}
                    {index === 0 && subjectLabels.length > 1 ? (
                      <span className="rubric-ed-header__subject-primary-mark">principal</span>
                    ) : null}
                  </li>
                ))
              ) : (
                <li className="rubric-ed-header__subject-item">Asignatura</li>
              )}
            </ul>
          </div>
        </div>

        <div className="rubric-ed-header__row rubric-ed-header__row--title">
          <div className="rubric-ed-header__badge">{typeShort}</div>
          <div className="rubric-ed-header__title-cell">
            <h1 className="rubric-ed-header__title">
              {title}
              {roleMeta ? ` · ${roleMeta.name}` : ''}
            </h1>
          </div>
          <div className="rubric-ed-header__profesor">
            <span className="rubric-ed-header__profesor-label">Profesor/a</span>
            {teacherLabel ? (
              <span className="rubric-ed-header__profesor-value">{teacherLabel}</span>
            ) : (
              <span className="rubric-ed-header__profesor-value rubric-ed-header__profesor-value--blank" aria-hidden />
            )}
          </div>
        </div>

        <div className="rubric-ed-header__row rubric-ed-header__row--fields">
          <div className="rubric-ed-header__field rubric-ed-header__field--name">
            <span className="rubric-ed-header__field-label">Nombre</span>
            <span className="rubric-ed-header__field-line" />
          </div>
          <div className="rubric-ed-header__field rubric-ed-header__field--course">
            <span className="rubric-ed-header__field-label">Curso</span>
            {courseFilled ? (
              <strong className="rubric-ed-header__field-value">{courseFilled}</strong>
            ) : (
              <span className="rubric-ed-header__field-line" />
            )}
          </div>
          <div className="rubric-ed-header__field rubric-ed-header__field--date">
            <span className="rubric-ed-header__field-label">Fecha</span>
            <span className="rubric-ed-header__field-line" />
          </div>
        </div>
      </header>

      <table className="rubric-ed-meta">
        <colgroup>
          <col className="rubric-ed-meta__col-eje" />
          <col className="rubric-ed-meta__col-oa" />
          <col className="rubric-ed-meta__col-score" />
          <col className="rubric-ed-meta__col-score" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Eje</th>
            <th scope="col">Objetivo de aprendizaje</th>
            <th scope="col">Máx.</th>
            <th scope="col">Obtenido</th>
          </tr>
        </thead>
        <tbody>
          {oaRows.map((row, index) => (
            <tr key={`oa-${index}`}>
              <td className="rubric-ed-meta__eje">{row.axis}</td>
              <td className="rubric-ed-meta__oa">
                {row.text}
                {index === 0 && showObjectiveExtra ? (
                  <p className="rubric-ed-meta__oa-extra">{printedObjective}</p>
                ) : null}
              </td>
              {index === 0 ? (
                <>
                  <td className="rubric-ed-meta__score-cell" rowSpan={oaRows.length}>
                    <span className="rubric-ed-meta__score-value">{maxScore || ''}</span>
                  </td>
                  <td
                    className="rubric-ed-meta__score-cell rubric-ed-meta__score-cell--blank"
                    rowSpan={oaRows.length}
                  >
                    <span className="rubric-ed-meta__score-blank" aria-hidden />
                  </td>
                </>
              ) : null}
            </tr>
          ))}
          <tr>
            <th scope="row" className="rubric-ed-meta__indicadores-label">
              Indicadores de evaluación
            </th>
            <td className="rubric-ed-meta__indicadores">
              {indicadores.length > 0 ? (
                <ul>
                  {indicadores.map((item) => (
                    <li key={item.id}>{item.name}</li>
                  ))}
                </ul>
              ) : (
                <span>Sin indicadores declarados.</span>
              )}
            </td>
            <td className="rubric-ed-meta__calificacion" colSpan={2}>
              <div className="rubric-ed-meta__calificacion-inner">
                <span className="rubric-ed-meta__calificacion-label">Calificación</span>
                <span className="rubric-ed-meta__calificacion-blank" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <table className="rubric-ed-matrix">
        <thead>
          <tr>
            <th scope="col" rowSpan={2} className="rubric-ed-matrix__corner">
              Criterio de evaluación
            </th>
            <th scope="col" colSpan={levelCount} className="rubric-ed-matrix__levels-head">
              Niveles de logro
            </th>
          </tr>
          <tr>
            {rubric.levels.map((level) => (
              <th key={level.id} scope="col" className="rubric-ed-matrix__level">
                <span className="rubric-ed-matrix__level-name">{level.name}</span>
                {level.points != null && Number.isFinite(Number(level.points)) ? (
                  <span className="rubric-ed-matrix__level-points">{level.points} pts</span>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {criteria.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="rubric-ed-matrix__empty">
                No hay criterios para este filtro de rol.
              </td>
            </tr>
          ) : (
            criterionGroups.map((group) => (
              <React.Fragment key={group.key}>
                {group.label ? (
                  <tr className="rubric-ed-matrix__category-row">
                    <th scope="rowgroup" colSpan={colSpan} className="rubric-ed-matrix__category">
                      {group.label}
                    </th>
                  </tr>
                ) : null}
                {group.criteria.map((criterion) => (
                  <tr key={criterion.id} className="rubric-ed-matrix__row">
                    <th scope="row" className="rubric-ed-matrix__criterion">
                      {criterion.name.trim() || 'Criterio sin nombre'}
                    </th>
                    {rubric.levels.map((level) => (
                      <td key={`${criterion.id}-${level.id}`} className="rubric-ed-matrix__cell">
                        {(criterion.cells[level.id] ?? '').trim() || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>

      <section className="rubric-ed-obs" aria-label="Observaciones">
        <h2 className="rubric-ed-obs__title">Observaciones</h2>
        <p className="rubric-ed-obs__lead">Observaciones generales:</p>
        <div className="rubric-ed-obs__box" aria-hidden>
          <span />
          <span />
          <span />
          <span />
        </div>
      </section>

      <footer className="print-worksheet__footer">
        <p>
          {criteria.length} criterios · {rubric.levels.length} niveles de logro
        </p>
        <p className="print-worksheet__footer-brand">Edusico</p>
      </footer>
    </article>
  );
}

export function PrintRubricDocument({
  rubric,
  courseDisplayName,
  courseLevelLabel,
  courseLevelId,
  subjectName,
  courseSubjectTag,
  courseAdditionalSubjectTags,
  schoolName,
  schoolLogoDataUrl,
  teacherName,
  roleFilter = 'all',
}: PrintRubricDocumentProps) {
  const title = rubric.title.trim() || 'Rúbrica de evaluación';
  const objective = rubric.objective.trim();
  const printedObjective = resolvePrintedObjective(rubric);
  const oas = resolveRubricOas(rubric);
  const projectRoles = rubric.projectRoles ?? [];
  const roleMeta = roleFilter !== 'all' ? projectRoles.find((role) => role.id === roleFilter) : null;
  const criteria =
    roleFilter === 'all'
      ? rubric.criteria
      : filterCriteriaForRoles(rubric.criteria, [roleFilter]);

  const criterionGroups = useMemo(
    () => groupCriteriaByDominantRole(criteria, projectRoles, roleFilter),
    [criteria, projectRoles, roleFilter],
  );

  const levelsLabel = formatRubricTargetLevelsHeader(
    rubric,
    courseLevelId || null,
    (id) => EXAM_LEVEL_OPTIONS.find((option) => option.id === id)?.label || id,
  );
  const courseFilled = [
    courseDisplayName?.trim(),
    levelsLabel || courseLevelLabel?.trim(),
  ]
    .filter(Boolean)
    .join(' · ');
  const schoolLabel = schoolName?.trim() || 'Establecimiento educacional';
  const teacherLabel = teacherName?.trim() || '';
  const courseAdditionalTags = Array.isArray(courseAdditionalSubjectTags)
    ? courseAdditionalSubjectTags
    : [];
  const primaryTag = String(courseSubjectTag || '').trim().toLowerCase();
  const subjectLabels = resolvePrintedSubjectTags(rubric, primaryTag, courseAdditionalTags)
    .map((tag) => getSubjectNameByTag(tag) || tag)
    .filter(Boolean);
  if (!subjectLabels.length && subjectName?.trim()) {
    subjectLabels.push(subjectName.trim());
  }
  const subjectLabel =
    subjectLabels.join(' · ') ||
    formatRubricSubjectHeader(
      rubric,
      primaryTag,
      getSubjectNameByTag,
      subjectName?.trim() || 'Asignatura',
      courseAdditionalTags,
    );
  const maxScore = computeMaxScore(criteria, rubric.levels);
  const typeLabel = rubricEvaluationTypeLabel(rubric.evaluationType);
  const useClassicLayout = oas.length === 0;

  if (useClassicLayout) {
    return (
      <ClassicRubricPrint
        rubric={rubric}
        title={title}
        printedObjective={printedObjective}
        typeLabel={typeLabel}
        courseFilled={courseFilled}
        subjectLabel={subjectLabel}
        levelsLabel={levelsLabel}
        roleMeta={roleMeta}
        criteria={criteria}
        criterionGroups={criterionGroups}
        maxScore={maxScore}
      />
    );
  }

  return (
    <EditorialRubricPrint
      rubric={rubric}
      title={title}
      objective={objective}
      printedObjective={printedObjective}
      oas={oas}
      courseFilled={courseFilled}
      schoolLabel={schoolLabel}
      schoolLogoDataUrl={schoolLogoDataUrl}
      teacherLabel={teacherLabel}
      subjectLabels={subjectLabels.length ? subjectLabels : [subjectLabel]}
      roleMeta={roleMeta}
      criteria={criteria}
      criterionGroups={criterionGroups}
      maxScore={maxScore}
    />
  );
}
